import PropTypes from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";
import { useUserData } from "./userContext";
import {
  personPvm_getStatusList,
  personPvm_list,
  personPvm_save,
} from "src/data/personPvmFunctions";
import { asiakasPerson_fetchLists } from "src/data/asiakas/asiakasClass";
import { getYYYYMMDD } from "src/components/js/statics1";
import { vehicle_fetchList } from "src/components/vehicle/VehicleFunctions";
import lomaSettingsClass from "src/pages/loma/components/lomaSettingsClass";
import LomaActionModal from "src/pages/loma/LomaActionModal/LomaActionModal";
import { GridContext } from "./GridContext/GridContext";
import { cache_clear, vehicle_cacheName } from "src/components/js/CacheFunctions";
import { isWeekend } from "date-fns";

const LomaContext = createContext();
export { LomaContext };

export const LomaProvider = ({ children, _daysCount = 30, _selectedDate = null }) => {
  const { token, ownerAsiakasId, isLoaded: userIsLoaded } = useUserData();
  const { isLoaded: gridIsLoaded } = useContext(GridContext) || {};
  const [lomaData, setLomaData] = useState();
  const [personList, setPersonList] = useState();
  const [statusList, setStatusList] = useState();
  const [vehicleList, setVehicleList] = useState();
  const [selectedDate, setSelectedDate] = useState(_selectedDate || new Date());
  const [lomaSettings, setLomaSettings] = useState();
  const [nonce, setNonce] = useState(0);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProgressValue, setIsLoadingProgressValue] = useState(-1);
  const [daysCount, setDaysCount] = useState(_daysCount);
  const [isInited, setIsInited] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const startTime = new Date(selectedDate);
  startTime.setDate(startTime.getDate());
  const endTime = new Date(selectedDate);
  endTime.setDate(endTime.getDate() + daysCount);

  useEffect(() => {
    if (!userIsLoaded || !ownerAsiakasId || gridIsLoaded === false) return;
    let isMounted = true;
    const _asynk = async () => {
      const _statusList = await fetchStatusList();
      if (isMounted) setStatusList(_statusList);
      const _vehicleList = await fetchVehicleList();
      if (isMounted) setVehicleList(_vehicleList);
      const _lomaSettings = new lomaSettingsClass();
      _lomaSettings.init();
      if (isMounted) setLomaSettings(_lomaSettings);
      if (isMounted) setIsInited(true);

      return () => {
        isMounted = false;
      };
    };
    _asynk();
  }, [userIsLoaded, ownerAsiakasId, gridIsLoaded]);

  useEffect(() => {
    if (!_selectedDate) return;
    setSelectedDate(_selectedDate);
  }, [_selectedDate]);

  useEffect(() => {
    if (!selectedDate || !userIsLoaded || !ownerAsiakasId || !isInited) return;
    let isMounted = true;
    // set startTime to selectedDate - daysCount
    const _asynk = async () => {
      setIsLoading(true);
      const _data = await fetchLomaData();
      if (isMounted) setLomaData(_data);
      const _personList = await fetchAndFilterPersonList();
      if (isMounted) setPersonList(_personList);

      await verifyLomaData(_data, _personList);

      if (isMounted) setIsLoading(false);
      if (isMounted) setIsLoaded(true);
    };
    _asynk();
    return () => {
      isMounted = false;
    };
  }, [selectedDate, userIsLoaded, ownerAsiakasId, isInited, nonce, daysCount]);

  async function verifyLomaData(_data, _personList) {
    // verify that lomaData contains all persons in personList for dates from now to 14 days ahead (max endTime)
    for (let i = 0; i <= 15; i++) {
      const _date = new Date();
      _date.setDate(_date.getDate() + i);
      if (_date > endTime || _date < startTime) continue;
      const _dateStr = getYYYYMMDD(_date);
      for (let j = 0; j < _personList.length; j++) {
        const _person = _personList[j];
        const _row = _data.find((row) => row.pvm === _dateStr && row.personId === _person.personId);
        if (!_row) {
          // Missing lomarow, create a new row
          const personPvmStatusId = isWeekend(_date)
            ? 6
            : _person.settings.getSetting(20)?.intVar || 1;
          const vehicle = isWeekend ? null : getDefaultVehicle(_person.personId);
          await personPvm_save(
            null,
            ownerAsiakasId,
            _person.personId,
            _dateStr,
            personPvmStatusId,
            null,
            vehicle?.vehicleId,
            token
          );
        }
      }
    }
  }

  async function reloadStatusList() {
    cache_clear("personPvm_statusList");
    const _statusList = await personPvm_getStatusList(ownerAsiakasId, token);
    setStatusList(_statusList);
  }

  async function reloadVehicles() {
    cache_clear(vehicle_cacheName);
    const _vehicleList = await vehicle_fetchList(ownerAsiakasId, token);
    setVehicleList(_vehicleList);
  }

  async function fetchLomaData() {
    const _newEndTime = new Date(endTime);
    _newEndTime.setDate(_newEndTime.getDate() + 1); // add one day
    const _data = await personPvm_list(ownerAsiakasId, startTime, _newEndTime, null, token);
    const _firstRow = { pvm: getYYYYMMDD(startTime) };
    const _lastRow = { pvm: getYYYYMMDD(_newEndTime) };
    _data.unshift(_firstRow);
    _data.push(_lastRow);
    return _data;
  }
  async function fetchAndFilterPersonList() {
    const _personList = await asiakasPerson_fetchLists(
      ownerAsiakasId,
      token,
      getYYYYMMDD(startTime)
    );
    // create a new personlist from _personList.personList where person has in asiakasPersonSettings
    // the following: asiakasPersonSettingTypeId = 15 and 9
    _personList.personList = _personList.personList.filter((person) => {
      if (person.asiakasPersonSettings) {
        // check if person has asiakasPersonSettingTypeId = 15 (Lomaseurannassa)
        const _asiakasPersonSetting1 = person.asiakasPersonSettings.find(
          (setting) => setting.asiakasPersonSettingTypeId === 15
        );
        if (!_asiakasPersonSetting1) return false;
        // check if startDate and endDate are valid
        if (_asiakasPersonSetting1.startDate && _asiakasPersonSetting1.startDate > endTime)
          return false;
        if (_asiakasPersonSetting1.endDate && _asiakasPersonSetting1.endDate < startTime)
          return false;
        // check if person has asiakasPersonSettingTypeId = 9 (Työsuhteessa)
        const _asiakasPersonSetting2 = person.asiakasPersonSettings.find(
          (setting) => setting.asiakasPersonSettingTypeId === 9
        );
        if (!_asiakasPersonSetting2) return false;
        if (_asiakasPersonSetting2.startDate && _asiakasPersonSetting2.startDate > endTime)
          return false;
        if (_asiakasPersonSetting2.endDate && _asiakasPersonSetting2.endDate < startTime)
          return false;
        return true;
      }
      return false;
    });
    // sort personList by personFirstName then by personLastName
    _personList.personList.sort((a, b) => {
      if (a.personFirstName < b.personFirstName) return -1;
      if (a.personFirstName > b.personFirstName) return 1;
      if (a.personLastName < b.personLastName) return -1;
      if (a.personLastName > b.personLastName) return 1;
      return 0;
    });
    return _personList.personList;
  }
  async function fetchStatusList() {
    const _statusList = await personPvm_getStatusList(ownerAsiakasId, token);
    return _statusList;
  }

  async function fetchVehicleList() {
    const _vehicleList = await vehicle_fetchList(ownerAsiakasId, token);
    return _vehicleList;
  }

  const getDefaultVehicle = (personId) => {
    const _vehicle = vehicleList.find((vehicle) => vehicle.defaultKuski_personId === personId);
    return _vehicle;
  };

  const reload = () => {
    setNonce(nonce + 1);
  };

  const doSetLomaSettings = (settings) => {
    setLomaSettings(settings);
    lomaSettingsClass.save(settings);
  };

  // vehicle has firstDate and lastDate, compare to current date
  const activeVehicleList =
    vehicleList &&
    vehicleList.filter((vehicle) => {
      const selectedDate = new Date();
      if (vehicle.firstDate && vehicle.firstDate > selectedDate) return false;
      if (vehicle.lastDate && vehicle.lastDate < selectedDate) return false;
      return true;
    });
  // sort vehicleList by sortNo
  activeVehicleList &&
    activeVehicleList.sort((a, b) => {
      if (a.sortNo < b.sortNo) return -1;
      if (a.sortNo > b.sortNo) return 1;
      return 0;
    });

  const lomaData_currentDay = lomaData?.filter((row) => row.pvm === getYYYYMMDD(selectedDate));

  return (
    <LomaContext.Provider
      value={{
        lomaData,
        lomaData_currentDay,
        personList,
        statusList,
        setStatusList,
        reloadStatusList,

        isLoaded,
        isLoading,
        setIsLoading,
        isLoadingProgressValue,
        setIsLoadingProgressValue,

        daysCount,
        setDaysCount,

        selectedDate,
        setSelectedDate,
        startTime,
        endTime,

        getDefaultVehicle,
        vehicleList_all: vehicleList,
        vehicleList_active: activeVehicleList,
        reload,
        reloadVehicles,

        lomaSettings,
        setLomaSettings: doSetLomaSettings,

        selectedAction, // Toiminto kun klikkaa solua: kysy
        setSelectedAction,

        isModalOpen,
        setIsModalOpen,
        selectedCell,
        setSelectedCell,
      }}>
      {children}
      <LomaActionModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </LomaContext.Provider>
  );
};

export const useLomaData = () => useContext(LomaContext);

LomaProvider.propTypes = {
  children: PropTypes.node.isRequired,
  _daysCount: PropTypes.number,
  _selectedDate: PropTypes.instanceOf(Date),
};
